import { parseFormula, type AtomCount } from '../formula';

// Shared helpers for the REACTIONS / STOICHIOMETRY widgets: parsing chemical
// equations into reactant/product species, computing atom tallies, and an exact
// integer balancer (Gaussian elimination over rationals → smallest integers).
// All functions are resilient: malformed input returns empty / null rather than
// throwing, so widgets can fall back to a default example.

export interface Species {
  formula: string; // e.g. "H2O"
  atoms: AtomCount[]; // parsed element counts for one unit
}

export interface ParsedEquation {
  reactants: Species[];
  products: Species[];
  /** All distinct element symbols across the whole equation, first-seen order. */
  elements: string[];
}

// Split an equation string into reactant / product species. Accepts "->", "=",
// "→", "⟶" as the yields arrow and "+" as the separator. Coefficients written
// inline (e.g. "2 H2") are ignored here — coefficients are the widget's job.
export function parseEquation(input: string): ParsedEquation | null {
  if (!input || typeof input !== 'string') return null;
  const norm = input.replace(/⟶|→|-->|->|=>|=/g, '|');
  const [lhs, rhs] = norm.split('|');
  if (!lhs || !rhs) return null;

  const toSpecies = (side: string): Species[] =>
    side
      .split('+')
      .map((t) => t.trim())
      // Drop any leading inline coefficient like "2H2O" or "2 H2O".
      .map((t) => t.replace(/^\s*\d+\s*/, '').trim())
      .filter(Boolean)
      .map((formula) => ({ formula, atoms: parseFormula(formula) }))
      .filter((s) => s.atoms.length > 0);

  const reactants = toSpecies(lhs);
  const products = toSpecies(rhs);
  if (reactants.length === 0 || products.length === 0) return null;

  const elements: string[] = [];
  for (const s of [...reactants, ...products])
    for (const a of s.atoms) if (!elements.includes(a.symbol)) elements.push(a.symbol);

  return { reactants, products, elements };
}

// Count of `symbol` atoms in one unit of a species (0 if absent).
export function atomsOf(species: Species, symbol: string): number {
  return species.atoms.find((a) => a.symbol === symbol)?.count ?? 0;
}

// Element tally for a side given per-species coefficients.
export function tally(side: Species[], coeffs: number[], elements: string[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const el of elements) {
    out[el] = side.reduce((sum, sp, i) => sum + atomsOf(sp, el) * (coeffs[i] ?? 0), 0);
  }
  return out;
}

// True when both sides have equal atom counts for every element (all coeffs > 0).
export function isBalanced(eq: ParsedEquation, react: number[], prod: number[]): boolean {
  if ([...react, ...prod].some((c) => !Number.isFinite(c) || c <= 0)) return false;
  const l = tally(eq.reactants, react, eq.elements);
  const r = tally(eq.products, prod, eq.elements);
  return eq.elements.every((el) => l[el] === r[el]);
}

// ── Exact integer balancer ────────────────────────────────────────────────
// Build the element-conservation matrix A·x = 0 (products negated), find the
// null-space vector via fraction-preserving Gaussian elimination, then scale to
// the smallest positive integers. Returns null if no clean solution is found.

interface Frac {
  n: number;
  d: number;
}
const gcd = (a: number, b: number): number => (b === 0 ? Math.abs(a) : gcd(b, a % b));
const lcm = (a: number, b: number): number => (a === 0 || b === 0 ? 0 : Math.abs((a / gcd(a, b)) * b));
function reduce(f: Frac): Frac {
  if (f.n === 0) return { n: 0, d: 1 };
  let g = gcd(f.n, f.d) || 1;
  let n = f.n / g;
  let d = f.d / g;
  if (d < 0) {
    n = -n;
    d = -d;
  }
  return { n, d };
}
const fadd = (a: Frac, b: Frac): Frac => reduce({ n: a.n * b.d + b.n * a.d, d: a.d * b.d });
const fsub = (a: Frac, b: Frac): Frac => fadd(a, { n: -b.n, d: b.d });
const fmul = (a: Frac, b: Frac): Frac => reduce({ n: a.n * b.n, d: a.d * b.d });
const fdiv = (a: Frac, b: Frac): Frac => reduce({ n: a.n * b.d, d: a.d * b.n });

export function balanceEquation(eq: ParsedEquation): number[] | null {
  const all = [...eq.reactants, ...eq.products];
  const m = all.length; // unknowns
  if (m < 2) return null;
  const rows = eq.elements.length;

  // A[el][species] with products negated → conservation as A·x = 0.
  const A: Frac[][] = eq.elements.map((el) =>
    all.map((sp, j) => {
      const sign = j < eq.reactants.length ? 1 : -1;
      return { n: atomsOf(sp, el) * sign, d: 1 };
    }),
  );

  // Gaussian elimination to reduced row-echelon form.
  const pivotCols: number[] = [];
  let r = 0;
  for (let c = 0; c < m && r < rows; c++) {
    let piv = -1;
    for (let i = r; i < rows; i++) if (A[i][c].n !== 0) { piv = i; break; }
    if (piv === -1) continue;
    [A[r], A[piv]] = [A[piv], A[r]];
    const inv = fdiv({ n: 1, d: 1 }, A[r][c]);
    for (let c2 = 0; c2 < m; c2++) A[r][c2] = fmul(A[r][c2], inv);
    for (let i = 0; i < rows; i++) {
      if (i === r) continue;
      const factor = A[i][c];
      if (factor.n === 0) continue;
      for (let c2 = 0; c2 < m; c2++) A[i][c2] = fsub(A[i][c2], fmul(factor, A[r][c2]));
    }
    pivotCols.push(c);
    r++;
  }

  // Expect exactly one free variable for a balanced reaction.
  const freeCols = Array.from({ length: m }, (_, i) => i).filter((c) => !pivotCols.includes(c));
  if (freeCols.length !== 1) return null;
  const free = freeCols[0];

  // Solution: free var = 1, pivots = -A[pivotRow][free].
  const x: Frac[] = Array.from({ length: m }, () => ({ n: 0, d: 1 }));
  x[free] = { n: 1, d: 1 };
  pivotCols.forEach((c, i) => {
    x[c] = reduce({ n: -A[i][free].n, d: A[i][free].d });
  });

  // Scale to smallest positive integers: multiply by LCM of denominators.
  let denomLcm = 1;
  for (const f of x) denomLcm = lcm(denomLcm, f.d) || denomLcm;
  let ints = x.map((f) => Math.round((f.n * denomLcm) / f.d));
  // Normalise sign so coefficients are positive, then divide by their GCD.
  if (ints.some((v) => v < 0) && ints.every((v) => v <= 0)) ints = ints.map((v) => -v);
  if (ints.some((v) => v < 0)) ints = ints.map((v) => Math.abs(v));
  let g = 0;
  for (const v of ints) g = gcd(g, v);
  if (g > 1) ints = ints.map((v) => v / g);

  if (ints.some((v) => v <= 0 || !Number.isFinite(v))) return null;
  return ints;
}

// Render subscripts as small digits (visual only) — mirrors FormulaBreakdown.
export function subscriptFormula(formula: string): string {
  return formula.replace(/(\d+)/g, (mm) =>
    mm
      .split('')
      .map((d) => '₀₁₂₃₄₅₆₇₈₉'[Number(d)])
      .join(''),
  );
}

// A curated set of reactions used as built-in examples across the widgets.
export const EXAMPLE_EQUATIONS: { label: string; equation: string }[] = [
  { label: 'Formation of water', equation: 'H2 + O2 -> H2O' },
  { label: 'Combustion of methane', equation: 'CH4 + O2 -> CO2 + H2O' },
  { label: 'Combustion of propane', equation: 'C3H8 + O2 -> CO2 + H2O' },
  { label: 'Synthesis of ammonia', equation: 'N2 + H2 -> NH3' },
  { label: 'Rusting of iron', equation: 'Fe + O2 -> Fe2O3' },
  { label: 'Photosynthesis', equation: 'CO2 + H2O -> C6H12O6 + O2' },
  { label: 'Neutralisation', equation: 'HCl + NaOH -> NaCl + H2O' },
  { label: 'Decomposition of water', equation: 'H2O -> H2 + O2' },
];
